/**
 * Vista Utenti - CRUD utenti (solo admin)
 */

import { getState, initStore } from '../store.js';
import * as api from '../api.js';
import {
  renderEmailRecipientsEditor,
  collectEmailRecipients,
  validateEmailRecipients,
  summarizeEmails,
} from './EmailRecipientsEditor.js';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'director', label: 'Direttore' },
  { value: 'organizer', label: 'Organizzatore' },
  { value: 'user', label: 'Medico (user)' },
];
const securityUiState = {
  selectedUserId: null,
  riskOnly: false,
  eventTypeFilter: '',
  dateFrom: '',
  dateTo: '',
};

/** Ultima lista utenti caricata nella vista admin (per aggiornamenti parziali senza full render). */
let adminUsersCache = [];

function instanceLabelFromEvent(e) {
  const d = e?.details && typeof e.details === 'object' ? e.details : {};
  return d.instance || '—';
}

function dateInputToIsoStart(s) {
  if (!s || typeof s !== 'string') return '';
  return `${s.trim()}T00:00:00.000Z`;
}

function dateInputToIsoEnd(s) {
  if (!s || typeof s !== 'string') return '';
  return `${s.trim()}T23:59:59.999Z`;
}

async function refreshUsersLastAccessTable(container) {
  const tbody = container?.querySelector?.('#users-last-access-tbody');
  if (!tbody) return;
  try {
    const rows = await api.apiGetSecurityUsersLastAccess();
    tbody.innerHTML = renderUsersLastAccessRows(rows, securityUiState.selectedUserId);
  } catch (_) {
    tbody.innerHTML = '<tr><td colspan="6" style="color: var(--danger);">Errore caricamento ultimi accessi.</td></tr>';
  }
}

async function refreshMonitoringSection(container) {
  await refreshUsersLastAccessTable(container);
  const uid = securityUiState.selectedUserId;
  if (!uid || !container?.querySelector?.('#monitoraggio-accessi')) {
    if (!container?.querySelector?.('#monitoraggio-accessi')) {
      await renderUsersView(container);
    }
    return;
  }
  const fromIso = securityUiState.dateFrom ? dateInputToIsoStart(securityUiState.dateFrom) : '';
  const toIso = securityUiState.dateTo ? dateInputToIsoEnd(securityUiState.dateTo) : '';
  try {
    const secSummary = await api.apiGetSecuritySummary(uid, securityUiState.riskOnly).catch(() => ({
      lastLogins: [],
      recentEvents: [],
      lastDevices: [],
      openAlerts: [],
      activeSessionsCount: 0,
    }));
    let events = secSummary.recentEvents || secSummary.lastLogins || [];
    if (securityUiState.eventTypeFilter || fromIso || toIso) {
      try {
        const page = await api.apiGetSecurityEventsPage({
          userId: uid,
          riskOnly: securityUiState.riskOnly,
          eventType: securityUiState.eventTypeFilter || undefined,
          fromCreatedAt: fromIso || undefined,
          toCreatedAt: toIso || undefined,
          limit: 50,
        });
        events = page.items || [];
      } catch (_) {
        events = secSummary.recentEvents || secSummary.lastLogins || [];
      }
    }
    const eventsBody = container.querySelector('#security-events-tbody');
    const devicesBody = container.querySelector('#security-devices-tbody');
    const badge = container.querySelector('#monitoring-active-sessions');
    const riskBtn = container.querySelector('#btn-risk-only');
    if (eventsBody) eventsBody.innerHTML = renderSecurityEvents(events);
    if (devicesBody) devicesBody.innerHTML = renderSecurityDevices(secSummary.lastDevices || [], adminUsersCache);
    if (badge) {
      const n = Number(secSummary.activeSessionsCount ?? 0);
      badge.textContent = Number.isFinite(n) && n > 0 ? String(n) : '';
      badge.style.display = n > 0 ? 'inline' : 'none';
    }
    if (riskBtn) {
      riskBtn.textContent = securityUiState.riskOnly ? 'Mostra tutto' : 'Solo anomalie';
    }
  } catch (_) {
    /* ignore */
  }
}

async function exportMonitoringEventsCsv(container) {
  const uid = securityUiState.selectedUserId;
  if (!uid) return;
  const fromIso = securityUiState.dateFrom ? dateInputToIsoStart(securityUiState.dateFrom) : '';
  const toIso = securityUiState.dateTo ? dateInputToIsoEnd(securityUiState.dateTo) : '';
  const rows = [];
  let cursorCreatedAt;
  let cursorId;
  const maxRows = 500;
  while (rows.length < maxRows) {
    const page = await api.apiGetSecurityEventsPage({
      userId: uid,
      riskOnly: securityUiState.riskOnly,
      eventType: securityUiState.eventTypeFilter || undefined,
      fromCreatedAt: fromIso || undefined,
      toCreatedAt: toIso || undefined,
      limit: 100,
      cursorCreatedAt,
      cursorId,
    });
    const items = page.items || [];
    rows.push(...items);
    const nc = page.nextCursor;
    if (!nc?.createdAt || !nc?.id || items.length === 0) break;
    cursorCreatedAt = nc.createdAt;
    cursorId = nc.id;
    if (items.length < 100) break;
  }
  const header = ['created_at', 'event_type', 'success', 'username_attempt', 'user_id', 'instance', 'ip', 'city', 'region', 'country', 'device_hash', 'user_agent', 'risk_flags'];
  const lines = [header.join(',')];
  for (const e of rows.slice(0, maxRows)) {
    const inst = instanceLabelFromEvent(e);
    const rf = Array.isArray(e.risk_flags) ? e.risk_flags.join(';') : '';
    const vals = [
      e.created_at,
      e.event_type,
      e.success ? '1' : '0',
      e.username_attempt,
      e.user_id,
      inst,
      e.ip,
      e.city,
      e.region,
      e.country,
      e.device_hash,
      e.user_agent,
      rf,
    ].map((v) => {
      const s = v == null ? '' : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    });
    lines.push(vals.join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `accessi-${uid.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function renderUsersView(container) {
  const state = getState();
  if (state.currentUser?.role !== 'admin') {
    container.innerHTML = '<div class="card"><p style="color: var(--danger);">Accesso negato.</p></div>';
    return;
  }

  container.innerHTML = '<div class="card"><p>Caricamento utenti...</p></div>';

  Promise.all([
    api.apiGetUsers(),
    api.apiListBackups().catch(() => []),
    api.getApiStatus().catch(() => null),
    api.apiGetSecurityUsersLastAccess().catch(() => []),
  ])
    .then(async ([users, backups, apiStatus, usersLastAccess]) => {
      if (!securityUiState.selectedUserId && users.length > 0) securityUiState.selectedUserId = users[0].id;
      const selected = securityUiState.selectedUserId;
      const secSummaryEmpty = {
        lastLogins: [],
        recentEvents: [],
        lastDevices: [],
        openAlerts: [],
        activeSessionsCount: 0,
      };
      const secSummary = selected
        ? await api.apiGetSecuritySummary(selected, securityUiState.riskOnly).catch(() => ({ ...secSummaryEmpty }))
        : { ...secSummaryEmpty };
      let monitoringEvents = secSummary.recentEvents || secSummary.lastLogins || [];
      const fromIsoPre = securityUiState.dateFrom ? dateInputToIsoStart(securityUiState.dateFrom) : '';
      const toIsoPre = securityUiState.dateTo ? dateInputToIsoEnd(securityUiState.dateTo) : '';
      if (selected && (securityUiState.eventTypeFilter || fromIsoPre || toIsoPre)) {
        try {
          const pagePre = await api.apiGetSecurityEventsPage({
            userId: selected,
            riskOnly: securityUiState.riskOnly,
            eventType: securityUiState.eventTypeFilter || undefined,
            fromCreatedAt: fromIsoPre || undefined,
            toCreatedAt: toIsoPre || undefined,
            limit: 50,
          });
          monitoringEvents = pagePre.items || monitoringEvents;
        } catch (_) {
          /* keep summary rows */
        }
      }
      const activeSessionsN = Number(secSummary.activeSessionsCount ?? 0);
      const activeBadgeText = Number.isFinite(activeSessionsN) && activeSessionsN > 0 ? String(activeSessionsN) : '';
      const activeBadgeDisplay = activeSessionsN > 0 ? 'inline' : 'none';
      const env = String(apiStatus?.env || '').toLowerCase();
      const channel = String(apiStatus?.channel || '').toLowerCase();
      const portBeta = typeof window !== 'undefined' && window.location?.port === '3300';
      const portAlpha = typeof window !== 'undefined' && window.location?.port === '3330';
      const hostAlpha =
        typeof window !== 'undefined' && /turnialfa/i.test(String(window.location.hostname || ''));
      const isBetaChannel =
        channel === 'beta' ||
        (!channel && /^(staging|beta|development)$/.test(env)) ||
        (apiStatus == null && portBeta);
      const isAlphaChannel = channel === 'alpha' || hostAlpha || (apiStatus == null && portAlpha);
      const upstreamSources = apiStatus?.syncUpstreamSources || {};
      const showLiveSyncUi = isBetaChannel;
      const liveSyncReady = isBetaChannel && !!apiStatus?.syncFromLiveAvailable;
      const showAlphaSyncUi = isAlphaChannel;
      const alphaLiveReady = isAlphaChannel && !!upstreamSources.live;
      const alphaBetaReady = isAlphaChannel && !!upstreamSources.beta;
      // La sync parziale usa le stesse sorgenti della sync integrale.
      const showAbsenceSyncUi = showLiveSyncUi || showAlphaSyncUi;
      container.innerHTML = `
        <div id="admin-panel-root">
        <div class="card" style="margin-bottom: 1rem;">
          <div class="card-title">Pannello amministrazione</div>
          <p style="font-size: 0.9rem; color: var(--text-secondary); margin: 0 0 0.5rem 0;">
            Gestione <strong>utenti</strong>, operazioni su <strong>database e sistema</strong>, e <strong>monitoraggio accessi</strong> (log e dispositivi).
          </p>
          <nav style="display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center;" aria-label="Sezioni pannello admin">
            <a class="admin-panel-nav-link" href="#gestione-utenti">Utenti</a>
            <a class="admin-panel-nav-link" href="#database-sistema">Database e sistema</a>
            <a class="admin-panel-nav-link" href="#monitoraggio-accessi">Monitoraggio accessi</a>
          </nav>
        </div>
        <div class="card users-admin-card" id="gestione-utenti">
          <div class="card-title">Gestione Utenti</div>
          <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
            Crea e modifica gli utenti dell'applicazione. Gli utenti con ruolo "Medico" devono essere collegati a un medico esistente. Ogni utente puo' avere piu' <strong>recapiti email</strong>: il principale serve al recupero password, gli altri solo alla consegna di PDF turni e comunicazioni. Gli organizzatori possono opzionalmente avere un medico collegato.
          </p>
          <button class="btn btn-primary" id="btn-new-user" style="margin-bottom: 1rem;">+ Nuovo utente</button>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Ruolo</th>
                  <th>Medico collegato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                ${users.map(u => `
                  <tr data-id="${u.id}">
                    <td>${escapeHtml(u.username)}</td>
                    <td>${summarizeEmails(u)}</td>
                    <td>${getRoleLabel(u.role)}</td>
                    <td>${u.doctorId ? (state.doctors.find(d => d.id === u.doctorId)?.sigla || u.doctorId) : '-'}</td>
                    <td>
                      <button class="btn btn-secondary btn-sm btn-edit-user" data-id="${u.id}">Modifica</button>
                      ${u.id !== state.currentUser?.id ? `<button class="btn btn-danger btn-sm btn-delete-user" data-id="${u.id}">Elimina</button>` : ''}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <div class="card users-backup-card" id="database-sistema" style="margin-top: 1.5rem;">
          <div class="card-title">Backup / Sincronizzazione</div>
          <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
            Salva checkpoint manuali e ripristina lo stato operativo (medici, assenze, turni, calendario, ecc.) a un backup precedente. La tabella <strong>utenti</strong> non viene mai modificata dai checkpoint JSON.
          </p>
          ${showLiveSyncUi ? `
          <div style="margin-bottom: 1.25rem; padding: 1rem; background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius); border-left: 4px solid var(--warning);">
            <div style="font-weight: 600; margin-bottom: 0.35rem;">Sincronizza da live → beta</div>
            <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0; line-height: 1.45;">
              Disponibile <strong>solo nella versione beta</strong> (mai su live). Copia l’intero file SQLite dalla versione <strong>live</strong> verso questo ambiente, <strong>sostituendo</strong> il database corrente (inclusi <strong>utenti e credenziali</strong> come sul live). Prima viene salvata una copia del DB beta sul server. Dopo l’operazione serve <strong>ricaricare la pagina</strong>.
            </p>
            <button type="button" class="btn btn-warning" id="btn-sync-from-live" ${liveSyncReady ? '' : 'disabled'} title="${liveSyncReady ? 'Avvia sincronizzazione da live' : 'Richiede configurazione sorgente live (remota via SSH o file locale)'}">Sincronizza database da live</button>
            ${!liveSyncReady ? `<p style="font-size: 0.82rem; color: var(--text-secondary); margin: 0.65rem 0 0 0; line-height: 1.4;">Il pulsante si attiva quando il backend espone <strong>syncFromLiveAvailable</strong>: configura una sorgente live remota (<strong>TURNI_LIVE_REMOTE_*</strong>) oppure un file locale leggibile nel container (<strong>TURNI_LIVE_DB_PATH</strong>).</p>` : ''}
          </div>
          ` : ''}
          ${showAlphaSyncUi ? `
          <div style="margin-bottom: 1.25rem; padding: 1rem; background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius); border-left: 4px solid var(--warning);">
            <div style="font-weight: 600; margin-bottom: 0.35rem;">Sincronizza database verso alfa</div>
            <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0; line-height: 1.45;">
              Sostituisci il database di <strong>questa</strong> istanza (alfa) con una copia integrale dalla <strong>live</strong> o dalla <strong>beta</strong> (solo lettura sul server). Vengono copiati anche utenti e password della sorgente scelta. Prima viene salvata una copia del DB alfa sul server.
            </p>
            <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
              <button type="button" class="btn btn-warning" id="btn-sync-alpha-from-live" ${alphaLiveReady ? '' : 'disabled'} title="${alphaLiveReady ? 'Sincronizza da live' : 'Configura TURNI_LIVE_REMOTE_* o TURNI_LIVE_DB_PATH'}">Da live → alfa</button>
              <button type="button" class="btn btn-warning" id="btn-sync-alpha-from-beta" ${alphaBetaReady ? '' : 'disabled'} title="${alphaBetaReady ? 'Sincronizza da beta' : 'Monta il DB beta (TURNI_BETA_DB_PATH) nel container alfa'}">Da beta → alfa</button>
            </div>
            ${!alphaLiveReady && !alphaBetaReady ? `<p style="font-size: 0.82rem; color: var(--text-secondary); margin: 0.65rem 0 0 0; line-height: 1.4;">Configura almeno una sorgente: live remota/file come per la beta, oppure mount del file SQLite della beta (<strong>TURNI_BETA_DB_PATH</strong>).</p>` : ''}
          </div>
          ` : ''}
          ${showAbsenceSyncUi ? `
          <div style="margin-bottom: 1.25rem; padding: 1rem; background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius); border-left: 4px solid var(--warning);">
            <div style="font-weight: 600; margin-bottom: 0.35rem;">Sincronizza solo le assenze</div>
            <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0; line-height: 1.45;">
              Copia dalla sorgente <strong>soltanto</strong> assenze, desiderata, indisponibilità ricorrenti (con le relative eccezioni) e settimane bloccate per le richieste, <strong>sostituendo</strong> quelle presenti qui. <strong>Non</strong> vengono toccati utenti, medici, turni, calendario e carichi: niente logout, nessun ricaricamento forzato. I medici vengono riconosciuti per <strong>sigla</strong>; le righe di medici non presenti in questo ambiente vengono ignorate e segnalate nell'esito. Prima dell'operazione viene salvato un <strong>checkpoint</strong> ripristinabile dalla tabella qui sotto.
            </p>
            <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
              ${showLiveSyncUi ? `<button type="button" class="btn btn-warning" id="btn-sync-absences-from-live" ${liveSyncReady ? '' : 'disabled'} title="${liveSyncReady ? 'Sincronizza le assenze da live' : 'Richiede configurazione sorgente live (remota via SSH o file locale)'}">Assenze da live \u2192 beta</button>` : ''}
              ${showAlphaSyncUi ? `<button type="button" class="btn btn-warning" id="btn-sync-absences-alpha-from-live" ${alphaLiveReady ? '' : 'disabled'} title="${alphaLiveReady ? 'Sincronizza le assenze da live' : 'Configura TURNI_LIVE_REMOTE_* o TURNI_LIVE_DB_PATH'}">Assenze da live \u2192 alfa</button>` : ''}
              ${showAlphaSyncUi ? `<button type="button" class="btn btn-warning" id="btn-sync-absences-alpha-from-beta" ${alphaBetaReady ? '' : 'disabled'} title="${alphaBetaReady ? 'Sincronizza le assenze da beta' : 'Monta il DB beta (TURNI_BETA_DB_PATH) nel container alfa'}">Assenze da beta \u2192 alfa</button>` : ''}
            </div>
          </div>
          ` : ''}
          <div style="margin-bottom: 1rem;">
            <button type="button" class="btn btn-secondary" id="btn-diagnostics-shifts" title="Diagnosi consistenza slot/assegnazioni/import">
              Diagnosi coerenza turni
            </button>
          </div>
          <div style="margin-bottom: 1.25rem; padding: 1rem; background: var(--bg-secondary); border: 1px solid var(--border); border-radius: var(--radius);">
            <div style="font-weight: 600; margin-bottom: 0.35rem;">Igiene database turni</div>
            <p style="font-size: 0.88rem; color: var(--text-secondary); margin: 0 0 0.75rem 0; line-height: 1.45;">
              Allinea le <strong>UOC</strong> degli slot al valore canonico della <strong>categoria</strong> (slot persistiti, template e modifiche calendario). Cerca <strong>duplicati di cella</strong> (stessa data, fascia, categoria, UOC, tipo attività e nome — anche con <strong>nome vuoto</strong> in database, come spesso in Piano turni); puoi unificare lasciando un solo slot per gruppo. Le modifiche al database richiedono <strong>conferma password admin</strong> (step-up).
            </p>
            <label style="display: flex; align-items: center; gap: 0.45rem; font-size: 0.88rem; margin-bottom: 0.65rem; cursor: pointer;">
              <input type="checkbox" id="hygiene-align-also-reconcile" checked />
              Dopo l’allinea UOC, unifica anche duplicati logici (stessa cella Piano turni)
            </label>
            <div style="display: flex; flex-wrap: wrap; gap: 0.5rem;">
              <button type="button" class="btn btn-secondary" id="btn-hygiene-align-uocs" title="Aggiorna UOC su tutti gli slot e sul calendario">
                Allinea UOC da categoria
              </button>
              <button type="button" class="btn btn-secondary" id="btn-hygiene-scan-same-name" title="Solo lettura: elenco duplicati di cella (include nome vuoto)">
                Cerca duplicati stessa cella
              </button>
            </div>
          </div>
          <div style="display: flex; gap: 1rem; align-items: flex-end; margin-bottom: 1rem; flex-wrap: wrap;">
            <div class="form-group" style="margin-bottom: 0;">
              <label for="backup-label">Nome checkpoint (opzionale)</label>
              <input type="text" id="backup-label" placeholder="es. Prima della revisione 2025" style="width: 280px; padding: 0.5rem;">
            </div>
            <button class="btn btn-primary" id="btn-save-checkpoint">Salva checkpoint</button>
          </div>
          <div class="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Etichetta</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="backups-tbody">
                ${backups.length === 0 ? '<tr><td colspan="3" style="color: var(--text-secondary);">Nessun backup disponibile. Salva un checkpoint per iniziare.</td></tr>' : backups.map(b => `
                  <tr>
                    <td>${escapeHtml(formatBackupDate(b.created_at) || b.id)}</td>
                    <td>${escapeHtml(b.label || '—')}</td>
                    <td>
                      <button class="btn btn-danger btn-sm btn-restore" data-id="${escapeHtml(b.id)}">Ripristina</button>
                      <button class="btn btn-secondary btn-sm btn-delete-backup" data-id="${escapeHtml(b.id)}" style="margin-left: 0.25rem;">Elimina</button>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>

        <div class="card" id="monitoraggio-accessi" style="margin-top: 1.5rem;">
          <div class="card-title">Monitoraggio accessi</div>
          <p style="font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem;">
            Tracciamento accessi e attività correlate all’account selezionato: data, piattaforma (istanza), tipo evento, rete, dispositivo. Gli eventi includono il campo <code>instance</code> (da <code>TURNI_INSTANCE_LABEL</code> o <code>TURNI_UI_CHANNEL</code>) per distinguere live/beta/alfa nei report e in export.
          </p>
          <p style="font-size: 0.88rem; font-weight: 600; margin: 0 0 0.5rem 0;">Ultimo accesso per utente registrato</p>
          <div class="table-wrapper users-last-access-table" style="margin-bottom: 1.25rem; max-height: 320px; overflow: auto;">
            <table>
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>Ruolo</th>
                  <th>Ultimo accesso</th>
                  <th>Piattaforma</th>
                  <th>IP / Località</th>
                  <th>Dispositivo</th>
                </tr>
              </thead>
              <tbody id="users-last-access-tbody">
                ${renderUsersLastAccessRows(usersLastAccess, securityUiState.selectedUserId)}
              </tbody>
            </table>
          </div>
          <p style="font-size: 0.88rem; font-weight: 600; margin: 0 0 0.5rem 0;">Dettaglio utente selezionato</p>
          <div style="display: flex; gap: 0.5rem; margin-bottom: 0.75rem; flex-wrap: wrap; align-items: center;">
            <select id="security-user-select" style="padding: 0.4rem; min-width: 220px;">
              ${users.map(u => `<option value="${escapeHtml(u.id)}" ${u.id === securityUiState.selectedUserId ? 'selected' : ''}>${escapeHtml(u.username)} (${escapeHtml(getRoleLabel(u.role))})</option>`).join('')}
            </select>
            <span id="monitoring-active-sessions" title="Sessioni JWT attive (non revocate, non scadute)" style="display: ${activeBadgeDisplay}; font-size: 0.82rem; padding: 0.15rem 0.45rem; border-radius: 999px; background: var(--bg-secondary); border: 1px solid var(--border);">${activeBadgeText ? escapeHtml(activeBadgeText) + ' sessioni' : ''}</span>
            <button class="btn btn-secondary btn-sm" id="btn-refresh-security">Aggiorna</button>
            <button class="btn btn-secondary btn-sm" id="btn-risk-only">${securityUiState.riskOnly ? 'Mostra tutto' : 'Solo anomalie'}</button>
            <button class="btn btn-secondary btn-sm" id="btn-security-details">Vedi tutto</button>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-export-monitoring-csv" title="Scarica CSV (max 500 righe)">Esporta CSV</button>
          </div>
          <div style="display: flex; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; align-items: flex-end;">
            <div class="form-group" style="margin-bottom: 0;">
              <label for="monitor-event-type" style="font-size: 0.82rem;">Tipo evento</label>
              <select id="monitor-event-type" style="padding: 0.35rem; min-width: 160px;">
                <option value="" ${!securityUiState.eventTypeFilter ? 'selected' : ''}>Tutti</option>
                <option value="login" ${securityUiState.eventTypeFilter === 'login' ? 'selected' : ''}>login</option>
                <option value="password_reset_request" ${securityUiState.eventTypeFilter === 'password_reset_request' ? 'selected' : ''}>password_reset_request</option>
                <option value="password_reset_complete" ${securityUiState.eventTypeFilter === 'password_reset_complete' ? 'selected' : ''}>password_reset_complete</option>
                <option value="session_revoke" ${securityUiState.eventTypeFilter === 'session_revoke' ? 'selected' : ''}>session_revoke</option>
                <option value="step_up" ${securityUiState.eventTypeFilter === 'step_up' ? 'selected' : ''}>step_up</option>
                <option value="sync_from_upstream" ${securityUiState.eventTypeFilter === 'sync_from_upstream' ? 'selected' : ''}>sync_from_upstream</option>
              </select>
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label for="monitor-date-from" style="font-size: 0.82rem;">Da data</label>
              <input type="date" id="monitor-date-from" value="${escapeHtml(securityUiState.dateFrom || '')}" style="padding: 0.35rem;">
            </div>
            <div class="form-group" style="margin-bottom: 0;">
              <label for="monitor-date-to" style="font-size: 0.82rem;">A data</label>
              <input type="date" id="monitor-date-to" value="${escapeHtml(securityUiState.dateTo || '')}" style="padding: 0.35rem;">
            </div>
          </div>
          <div class="table-wrapper" style="margin-bottom: 1rem; max-height: 280px; overflow: auto;">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Piattaforma</th>
                  <th>Tipo</th>
                  <th>Utente</th>
                  <th>Esito</th>
                  <th>IP / Località</th>
                  <th>Dispositivo</th>
                  <th>Rischio</th>
                </tr>
              </thead>
              <tbody id="security-events-tbody">
                ${renderSecurityEvents(monitoringEvents)}
              </tbody>
            </table>
          </div>
          <div class="table-wrapper" style="max-height: 230px; overflow: auto;">
            <table>
              <thead>
                <tr>
                  <th>Utente</th>
                  <th>First seen</th>
                  <th>Last seen</th>
                  <th>IP / Località</th>
                  <th>Fingerprint</th>
                </tr>
              </thead>
              <tbody id="security-devices-tbody">
                ${renderSecurityDevices(secSummary.lastDevices || [], users)}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        <div id="restore-confirm-modal" style="display: none;"></div>
      `;

      container.querySelector('#btn-new-user')?.addEventListener('click', () => showUserForm(container, null));
      container.querySelector('#btn-sync-from-live')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncUpstreamConfirmModal(container, () => renderUsersView(container), { source: 'live', targetLabel: 'beta' });
      });
      container.querySelector('#btn-sync-alpha-from-live')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncUpstreamConfirmModal(container, () => renderUsersView(container), { source: 'live', targetLabel: 'alfa' });
      });
      container.querySelector('#btn-sync-alpha-from-beta')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncUpstreamConfirmModal(container, () => renderUsersView(container), { source: 'beta', targetLabel: 'alfa' });
      });
      container.querySelector('#btn-sync-absences-from-live')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncAbsencesConfirmModal(container, () => renderUsersView(container), { source: 'live', targetLabel: 'beta' });
      });
      container.querySelector('#btn-sync-absences-alpha-from-live')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncAbsencesConfirmModal(container, () => renderUsersView(container), { source: 'live', targetLabel: 'alfa' });
      });
      container.querySelector('#btn-sync-absences-alpha-from-beta')?.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        if (btn instanceof HTMLButtonElement && btn.disabled) return;
        showSyncAbsencesConfirmModal(container, () => renderUsersView(container), { source: 'beta', targetLabel: 'alfa' });
      });
      container.querySelector('#btn-diagnostics-shifts')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (!(btn instanceof HTMLButtonElement)) return;
        const prev = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Diagnosi in corso...';
        try {
          const diag = await api.apiGetShiftSlotsConsistencyDiagnostics();
          const s = diag?.summary || {};
          const lines = [
            'Diagnosi coerenza turni',
            '',
            `- Duplicate slot_key: ${Number(s.duplicateSlotKeys || 0)}`,
            `- Duplicate celle logiche: ${Number(s.duplicateLogicalCells || 0)}`,
            `- Assegnazioni orfane: ${Number(s.orphanAssignments || 0)}`,
            `- Warning import orfani: ${Number(s.orphanImportIssues || 0)}`,
            `- Slot non calendar-backed: ${Number(s.nonCalendarBackedSlots || 0)}`,
            `- Assegnazioni su slot non calendar-backed: ${Number(s.nonCalendarBackedAssigned || 0)}`,
            `- Vincolo DB attivo (idx_shift_slots_logical_unique): ${s.logicalUniqueIndexPresent ? 'SI' : 'NO'}`,
          ];
          alert(lines.join('\n'));
        } catch (err) {
          alert(err?.message || err);
        } finally {
          btn.disabled = false;
          btn.textContent = prev || 'Diagnosi coerenza turni';
        }
      });

      container.querySelector('#btn-hygiene-align-uocs')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (!(btn instanceof HTMLButtonElement)) return;
        if (!confirm(
          'Eseguire l’igiene: valorizzare i nomi slot vuoti (default da categoria/fascia), allineare le UOC al valore previsto per ciascuna categoria, e opzionalmente deduplicare?\n'
          + 'È consigliato aver salvato un checkpoint prima di procedere.',
        )) return;
        const also = !!container.querySelector('#hygiene-align-also-reconcile')?.checked;
        const ok = await ensureStepUp();
        if (!ok) return;
        const prev = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Allineamento…';
        try {
          const res = await api.apiPostShiftSlotsHygieneAlignUocs({ alsoReconcileLogicalDuplicates: also });
          await initStore();
          const rec = res?.reconcile || {};
          const nb = res?.nameBackfill || {};
          alert(
            `Igiene completata.\n`
            + `- Nomi valorizzati (erano vuoti): slot ${Number(nb.shift_slots ?? 0)}, template ${Number(nb.calendar_templates ?? 0)}, modifiche ${Number(nb.calendar_modifications ?? 0)}\n`
            + `- Righe UOC slot aggiornate: ${Number(res?.shiftSlotRowsUpdated ?? 0)}\n`
            + `- Template calendario (UOC): ${Number(res?.calendarTemplateRowsUpdated ?? 0)}\n`
            + `- Modifiche calendario (UOC): ${Number(res?.calendarModificationRowsUpdated ?? 0)}\n`
            + (also
              ? `- Deduplica logica: rimossi ${Number(rec?.deleted_slots ?? 0)} slot in ${Number(rec?.groups ?? 0)} gruppi (assegnazioni spostate: ${Number(rec?.moved_assignments ?? 0)})\n`
              : '')
            + '\nDati aggiornati in memoria; ricarica le altre schede se necessario.',
          );
        } catch (err) {
          alert(err?.message || String(err));
        } finally {
          btn.disabled = false;
          btn.textContent = prev || 'Allinea UOC da categoria';
        }
      });

      container.querySelector('#btn-hygiene-scan-same-name')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (!(btn instanceof HTMLButtonElement)) return;
        const prev = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Scansione…';
        try {
          const data = await api.apiGetShiftSlotsHygieneSameNameDuplicates();
          showSameNameDedupeModal(data, () => renderUsersView(container));
        } catch (err) {
          alert(err?.message || String(err));
        } finally {
          btn.disabled = false;
          btn.textContent = prev || 'Cerca duplicati stessa cella';
        }
      });
      container.querySelector('#btn-save-checkpoint')?.addEventListener('click', async () => {
        const labelInput = container.querySelector('#backup-label');
        const label = labelInput?.value?.trim() || '';
        container.querySelector('#btn-save-checkpoint').disabled = true;
        try {
          const ok = await ensureStepUp();
          if (!ok) return;
          await api.apiCreateBackup(label);
          labelInput && (labelInput.value = '');
          renderUsersView(container);
        } catch (err) {
          alert(err?.message || err);
        } finally {
          const btn = container.querySelector('#btn-save-checkpoint');
          if (btn) btn.disabled = false;
        }
      });
      container.querySelectorAll('.btn-restore').forEach(btn => {
        btn.addEventListener('click', () => showRestoreConfirmModal(container, btn.dataset.id, () => renderUsersView(container)));
      });
      container.querySelectorAll('.btn-delete-backup').forEach(btn => {
        btn.addEventListener('click', async () => {
          const id = btn.dataset.id;
          if (confirm('Eliminare questo backup? L\'operazione non è reversibile.')) {
            try {
              const ok = await ensureStepUp();
              if (!ok) return;
              await api.apiDeleteBackup(id);
              renderUsersView(container);
            } catch (err) {
              alert(err?.message || err);
            }
          }
        });
      });
      container.querySelectorAll('.btn-edit-user').forEach(btn => {
        btn.addEventListener('click', () => {
          const user = users.find(u => u.id === btn.dataset.id);
          if (user) showUserForm(container, user);
        });
      });
      container.querySelectorAll('.btn-delete-user').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.id;
          if (confirm('Eliminare questo utente?')) {
            api.apiDeleteUser(id).then(() => renderUsersView(container)).catch(err => alert(err?.message || err));
          }
        });
      });
      adminUsersCache = users;
      container.querySelector('#monitoraggio-accessi')?.addEventListener('click', (e) => {
        const tr = e.target.closest?.('.users-last-access-row');
        if (!tr?.dataset?.userId) return;
        securityUiState.selectedUserId = tr.dataset.userId;
        const sel = container.querySelector('#security-user-select');
        if (sel) sel.value = tr.dataset.userId;
        refreshMonitoringSection(container);
      });
      container.querySelector('#btn-refresh-security')?.addEventListener('click', () => refreshMonitoringSection(container));
      container.querySelector('#security-user-select')?.addEventListener('change', (e) => {
        securityUiState.selectedUserId = e.target.value || null;
        refreshMonitoringSection(container);
      });
      container.querySelector('#btn-risk-only')?.addEventListener('click', async () => {
        securityUiState.riskOnly = !securityUiState.riskOnly;
        refreshMonitoringSection(container);
      });
      container.querySelector('#monitor-event-type')?.addEventListener('change', (e) => {
        securityUiState.eventTypeFilter = (e.target && e.target.value) || '';
        refreshMonitoringSection(container);
      });
      container.querySelector('#monitor-date-from')?.addEventListener('change', (e) => {
        securityUiState.dateFrom = (e.target && e.target.value) || '';
        refreshMonitoringSection(container);
      });
      container.querySelector('#monitor-date-to')?.addEventListener('change', (e) => {
        securityUiState.dateTo = (e.target && e.target.value) || '';
        refreshMonitoringSection(container);
      });
      container.querySelector('#btn-export-monitoring-csv')?.addEventListener('click', () => {
        exportMonitoringEventsCsv(container).catch((err) => alert(err?.message || err));
      });
      container.querySelector('#btn-security-details')?.addEventListener('click', async () => {
        const userId = securityUiState.selectedUserId;
        if (!userId) return;
        const user = users.find(u => u.id === userId);
        try {
          const fromIsoM = securityUiState.dateFrom ? dateInputToIsoStart(securityUiState.dateFrom) : '';
          const toIsoM = securityUiState.dateTo ? dateInputToIsoEnd(securityUiState.dateTo) : '';
          const [eventsPage, devicesPage, alertsPage, sessionsPage] = await Promise.all([
            api.apiGetSecurityEventsPage({
              userId,
              riskOnly: securityUiState.riskOnly,
              eventType: securityUiState.eventTypeFilter || undefined,
              fromCreatedAt: fromIsoM || undefined,
              toCreatedAt: toIsoM || undefined,
              limit: 30,
            }),
            api.apiGetSecurityDevicesPage({ userId, limit: 30 }),
            api.apiGetSecurityAlertsPage({ limit: 30 }),
            api.apiGetSecuritySessionsPage({ userId, limit: 30 }),
          ]);
          showSecurityDetailsModal(container, user, {
            events: eventsPage.items || [],
            devices: devicesPage.items || [],
            alerts: alertsPage.items || [],
            sessions: sessionsPage.items || [],
            eventsNextCursor: eventsPage.nextCursor || null,
            devicesNextCursor: devicesPage.nextCursor || null,
            alertsNextCursor: alertsPage.nextCursor || null,
            sessionsNextCursor: sessionsPage.nextCursor || null,
          });
        } catch (err) {
          alert(err?.message || err);
        }
      });
      bindRevokeSessionButtons(container, () => {
        refreshMonitoringSection(container).catch(() => {});
      });
    })
    .catch(err => {
      const msg = err?.message || String(err);
      const is502 = /API 502/.test(msg);
      const hint = is502 ? '<p style="margin-top: 1rem; font-size: 0.9rem;">Controlla che il backend sia in esecuzione (es. <code>./start_server.sh</code> dalla cartella del progetto).</p>' : '';
      container.innerHTML = `<div class="card"><div class="card-title" style="color: var(--danger);">Errore</div><p>${escapeHtml(msg)}</p>${hint}</div>`;
    });
}

function formatBackupDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** Popup esito sincronizzazione DB upstream (successo o errore). */
function showSyncFromLiveOutcomeModal({ success, title, bodyText, onDismiss }) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.style.zIndex = '10050';
  const titleColor = success ? 'var(--success)' : 'var(--danger)';
  overlay.innerHTML = `
    <div class="modal" style="max-width: 440px;">
      <div class="modal-header">
        <h3 style="margin:0;font-size:1.05rem;color:${titleColor};">${escapeHtml(title)}</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="sync-outcome-close-x" aria-label="Chiudi">×</button>
      </div>
      <div class="modal-body">
        <p id="sync-outcome-body" style="margin:0;line-height:1.5;font-size:0.95rem;white-space:pre-wrap;"></p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" id="sync-outcome-ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const bodyEl = overlay.querySelector('#sync-outcome-body');
  if (bodyEl) bodyEl.textContent = bodyText || '';
  const dismiss = () => {
    overlay.remove();
    onDismiss?.();
  };
  overlay.querySelector('#sync-outcome-ok')?.addEventListener('click', dismiss);
  overlay.querySelector('#sync-outcome-close-x')?.addEventListener('click', dismiss);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) dismiss();
  });
}

/**
 * @param {{ source: 'live'|'beta', targetLabel: 'beta'|'alfa' }} opts
 */
function showSyncUpstreamConfirmModal(container, onSuccess, opts) {
  const source = opts.source === 'beta' ? 'beta' : 'live';
  const targetLabel = opts.targetLabel || 'beta';
  const sourceLabel = source === 'live' ? 'live' : 'beta';
  let modal = container.querySelector('#sync-from-live-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'sync-from-live-modal';
    container.appendChild(modal);
  }
  modal.style.display = 'block';
  modal.innerHTML = `
    <div class="card" style="max-width: 460px; margin: 1rem auto; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <div class="card-title" style="color: var(--warning);">Conferma sincronizzazione da ${sourceLabel}</div>
      <p style="font-size: 0.9rem; margin-bottom: 1rem;">
        Il file database di <strong>questa</strong> istanza (${targetLabel}) sarà sostituito con una copia integrale del database <strong>${sourceLabel}</strong> (tabelle operative <strong>e</strong> utenti/password come nella sorgente). I dati attuali vengono copiati in un file sul server prima dell’operazione.
      </p>
      <p style="font-size: 0.9rem; font-weight: 600; margin-bottom: 0.5rem;">Digita <strong>SINCRONIZZA</strong> per confermare:</p>
      <input type="text" id="sync-live-confirm-input" placeholder="SINCRONIZZA" style="width: 100%; padding: 0.5rem; margin-bottom: 1rem;" autocomplete="off">
      <div id="sync-live-error" style="color: var(--danger); font-size: 0.9rem; display: none; margin-bottom: 0.5rem;"></div>
      <div style="display: flex; gap: 0.5rem; justify-content: flex-end;">
        <button type="button" class="btn btn-secondary" id="sync-live-cancel">Annulla</button>
        <button type="button" class="btn btn-warning" id="sync-live-confirm-btn">Sincronizza</button>
      </div>
    </div>
  `;
  modal.querySelector('#sync-live-cancel').addEventListener('click', () => {
    modal.style.display = 'none';
    modal.innerHTML = '';
  });
  modal.querySelector('#sync-live-confirm-btn').addEventListener('click', async () => {
    const input = modal.querySelector('#sync-live-confirm-input');
    const errEl = modal.querySelector('#sync-live-error');
    const confirmText = (input?.value || '').trim();
    if (confirmText.toUpperCase() !== 'SINCRONIZZA') {
      errEl.textContent = 'Digita SINCRONIZZA per confermare.';
      errEl.style.display = 'block';
      return;
    }
    errEl.style.display = 'none';
    modal.querySelector('#sync-live-confirm-btn').disabled = true;
    try {
      const ok = await ensureStepUp();
      if (!ok) {
        modal.querySelector('#sync-live-confirm-btn').disabled = false;
        return;
      }
      const data = await api.apiSyncFromLive(confirmText, source);
      modal.style.display = 'none';
      modal.innerHTML = '';
      await initStore();
      onSuccess?.();
      const okLine =
        source === 'live' && targetLabel === 'beta'
          ? 'Il pull del database da live a beta è andato a buon fine.'
          : `Il pull del database da ${sourceLabel} a ${targetLabel} è andato a buon fine.`;
      const detailLines = [
        okLine,
        'Ricarica la pagina e accedi di nuovo con le credenziali presenti nel database sincronizzato.',
        data?.backupPath ? `Copia di sicurezza del DB precedente: ${data.backupPath}` : null,
      ].filter(Boolean);
      showSyncFromLiveOutcomeModal({
        success: true,
        title: 'Sincronizzazione riuscita',
        bodyText: detailLines.join('\n\n'),
        onDismiss: () => {
          api.clearAuth();
          window.location.reload();
        },
      });
    } catch (err) {
      const msg = err?.message || String(err);
      modal.style.display = 'none';
      modal.innerHTML = '';
      showSyncFromLiveOutcomeModal({
        success: false,
        title: 'Sincronizzazione non riuscita',
        bodyText:
          `Il pull del database da ${sourceLabel} a ${targetLabel} non è stato completato.\n\n` +
          'Dettaglio:\n' +
          msg,
        onDismiss: () => {
          onSuccess?.();
        },
      });
    }
  });
  modal.querySelector('#sync-live-confirm-input')?.focus();
}

const ABSENCE_SYNC_TABLE_LABELS = {
  absences: 'Assenze',
  desiderata: 'Desiderata',
  doctor_recurring_exclusions: 'Indisponibilit\u00e0 ricorrenti',
  recurring_exclusion_overrides: 'Eccezioni alle ricorrenti',
  absence_request_locked_weeks: 'Settimane bloccate per le richieste',
};

/** Riepilogo leggibile dell'esito di una sync assenze. */
function formatAbsenceSyncSummary(data) {
  const counts = data?.counts || {};
  const lines = Object.keys(ABSENCE_SYNC_TABLE_LABELS)
    .filter((k) => counts[k] != null)
    .map((k) => `\u2022 ${ABSENCE_SYNC_TABLE_LABELS[k]}: ${counts[k]} righe`);
  const skipped = data?.skippedRows || {};
  const skippedTotal = Object.values(skipped).reduce((acc, n) => acc + (Number(n) || 0), 0);
  if (skippedTotal > 0) {
    lines.push(`\u2022 Righe ignorate: ${skippedTotal}`);
  }
  const missing = Array.isArray(data?.missingDoctors) ? data.missingDoctors : [];
  if (missing.length) {
    lines.push(`\u2022 Medici non presenti in questo ambiente: ${missing.join(', ')}`);
  }
  const skippedTables = Array.isArray(data?.skippedTables) ? data.skippedTables : [];
  if (skippedTables.length) {
    lines.push(`\u2022 Tabelle assenti nella sorgente (lasciate invariate): ${skippedTables.join(', ')}`);
  }
  return lines.join('\n');
}

/**
 * Sync parziale: copia solo le tabelle delle assenze. Non tocca utenti/turni,
 * quindi niente logout \u2014 basta ricaricare lo store.
 * @param {{ source: 'live'|'beta', targetLabel: 'beta'|'alfa' }} opts
 */
function showSyncAbsencesConfirmModal(container, onSuccess, opts) {
  const source = opts.source === 'beta' ? 'beta' : 'live';
  const targetLabel = opts.targetLabel || 'beta';
  const sourceLabel = source === 'live' ? 'live' : 'beta';
  let modal = container.querySelector('#sync-absences-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'sync-absences-modal';
    container.appendChild(modal);
  }
  modal.style.display = 'block';
  modal.innerHTML = `
    <div class="card" style="max-width: 460px; margin: 1rem auto; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <div class="card-title" style="color: var(--warning);">Conferma sincronizzazione assenze da ${sourceLabel}</div>
      <p style="font-size: 0.9rem; margin-bottom: 1rem;">
        Assenze, desiderata, indisponibilit\u00e0 ricorrenti e settimane bloccate di <strong>questa</strong> istanza (${targetLabel}) saranno <strong>sostituite</strong> con quelle della sorgente <strong>${sourceLabel}</strong>. Utenti, medici, turni e calendario restano invariati. Prima dell'operazione viene salvato un checkpoint ripristinabile.
      </p>
      <p style="font-size: 0.9rem; font-weight: 600; margin-bottom: 0.5rem;">Digita <strong>SINCRONIZZA</strong> per confermare:</p>
      <input type="text" id="sync-absences-confirm-input" placeholder="SINCRONIZZA" style="width: 100%; padding: 0.5rem; margin-bottom: 1rem;" autocomplete="off">
      <div id="sync-absences-error" style="color: var(--danger); font-size: 0.9rem; display: none; margin-bottom: 0.5rem;"></div>
      <div style="display: flex; gap: 0.5rem; justify-content: flex-end;">
        <button type="button" class="btn btn-secondary" id="sync-absences-cancel">Annulla</button>
        <button type="button" class="btn btn-warning" id="sync-absences-confirm-btn">Sincronizza assenze</button>
      </div>
    </div>
  `;
  const closeModal = () => {
    modal.style.display = 'none';
    modal.innerHTML = '';
  };
  modal.querySelector('#sync-absences-cancel').addEventListener('click', closeModal);
  modal.querySelector('#sync-absences-confirm-btn').addEventListener('click', async () => {
    const input = modal.querySelector('#sync-absences-confirm-input');
    const errEl = modal.querySelector('#sync-absences-error');
    const confirmBtn = modal.querySelector('#sync-absences-confirm-btn');
    const confirmText = (input?.value || '').trim();
    if (confirmText.toUpperCase() !== 'SINCRONIZZA') {
      errEl.textContent = 'Digita SINCRONIZZA per confermare.';
      errEl.style.display = 'block';
      return;
    }
    errEl.style.display = 'none';
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Sincronizzazione...';
    try {
      const ok = await ensureStepUp();
      if (!ok) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = 'Sincronizza assenze';
        return;
      }
      const data = await api.apiSyncAbsencesFromUpstream(confirmText, source);
      closeModal();
      await initStore();
      onSuccess?.();
      const detailLines = [
        `Le assenze sono state sincronizzate da ${sourceLabel} a ${targetLabel}.`,
        formatAbsenceSyncSummary(data),
        data?.backupId ? `Checkpoint salvato prima dell'operazione: ${data.backupId}` : null,
      ].filter(Boolean);
      showSyncFromLiveOutcomeModal({
        success: true,
        title: 'Sincronizzazione assenze riuscita',
        bodyText: detailLines.join('\n\n'),
      });
    } catch (err) {
      const msg = err?.message || String(err);
      closeModal();
      showSyncFromLiveOutcomeModal({
        success: false,
        title: 'Sincronizzazione assenze non riuscita',
        bodyText:
          `La copia delle assenze da ${sourceLabel} a ${targetLabel} non \u00e8 stata completata.\n\n` +
          'Nessun dato \u00e8 stato modificato.\n\nDettaglio:\n' +
          msg,
        onDismiss: () => {
          onSuccess?.();
        },
      });
    }
  });
  modal.querySelector('#sync-absences-confirm-input')?.focus();
}

function showRestoreConfirmModal(container, backupId, onSuccess) {
  const modal = container.querySelector('#restore-confirm-modal') || document.createElement('div');
  modal.id = 'restore-confirm-modal';
  modal.style.display = 'block';
  modal.innerHTML = `
    <div class="card" style="max-width: 420px; margin: 1rem auto; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
      <div class="card-title" style="color: var(--danger);">Conferma ripristino</div>
      <p style="font-size: 0.9rem; margin-bottom: 1rem;">
        Stai per ripristinare i dati operativi (medici, assenze, turni, calendario, ecc.) allo stato salvato. Gli utenti non verranno modificati.
      </p>
      <p style="font-size: 0.9rem; font-weight: 600; margin-bottom: 0.5rem;">Digita <strong>RIPRISTINA</strong> per confermare:</p>
      <input type="text" id="restore-confirm-input" placeholder="RIPRISTINA" style="width: 100%; padding: 0.5rem; margin-bottom: 1rem;" autocomplete="off">
      <div id="restore-error" style="color: var(--danger); font-size: 0.9rem; display: none; margin-bottom: 0.5rem;"></div>
      <div style="display: flex; gap: 0.5rem; justify-content: flex-end;">
        <button type="button" class="btn btn-secondary" id="restore-cancel">Annulla</button>
        <button type="button" class="btn btn-danger" id="restore-confirm-btn">Ripristina</button>
      </div>
    </div>
  `;
  if (!container.contains(modal)) container.appendChild(modal);

  modal.querySelector('#restore-cancel').addEventListener('click', () => {
    modal.style.display = 'none';
    modal.innerHTML = '';
  });
  modal.querySelector('#restore-confirm-btn').addEventListener('click', async () => {
    const input = modal.querySelector('#restore-confirm-input');
    const errEl = modal.querySelector('#restore-error');
    const confirmText = (input?.value || '').trim();
    if (confirmText.toUpperCase() !== 'RIPRISTINA') {
      errEl.textContent = 'Digita RIPRISTINA per confermare.';
      errEl.style.display = 'block';
      return;
    }
    errEl.style.display = 'none';
    modal.querySelector('#restore-confirm-btn').disabled = true;
    try {
      const ok = await ensureStepUp();
      if (!ok) {
        modal.querySelector('#restore-confirm-btn').disabled = false;
        return;
      }
      await api.apiRestoreBackup(backupId, confirmText);
      modal.style.display = 'none';
      modal.innerHTML = '';
      await initStore();
      onSuccess?.();
      alert('Ripristino completato. Ricarica la pagina per vedere i dati aggiornati.');
      window.location.reload();
    } catch (err) {
      errEl.textContent = err?.message || err;
      errEl.style.display = 'block';
      modal.querySelector('#restore-confirm-btn').disabled = false;
    }
  });
  modal.querySelector('#restore-confirm-input')?.focus();
}

function getRoleLabel(role) {
  return ROLES.find(r => r.value === role)?.label || role;
}

function escapeHtml(s) {
  if (s == null) return '';
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

/**
 * @param {{ groups?: any[], groupCount?: number, totalExtraSlots?: number }} data
 * @param {() => void} [onDone]
 */
function showSameNameDedupeModal(data, onDone) {
  const groups = data?.groups || [];
  const groupCount = Number(data?.groupCount ?? 0);
  const totalExtra = Number(data?.totalExtraSlots ?? 0);
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  const listHtml = groups.map((g) => {
    const conflict = g.hasDoctorConflict
      ? '<span style="color:var(--danger);font-size:0.82rem;margin-left:0.35rem;">(medici diversi)</span>'
      : '';
    const rowCells = (g.slots || []).map(s => `
      <tr>
        <td style="font-size:0.78rem;word-break:break-all;">${escapeHtml(s.id || '')}</td>
        <td>${escapeHtml(s.category || '')}</td>
        <td style="font-size:0.78rem;">${escapeHtml(s.uocId || '')}</td>
        <td>${escapeHtml(s.doctorSigla || '—')}</td>
        <td>${s.locked ? 'Sì' : '—'}</td>
      </tr>
    `).join('');
    return `
      <details style="margin-bottom:0.65rem;border:1px solid var(--border);border-radius:var(--radius);padding:0.5rem 0.65rem;background:var(--bg);">
        <summary style="cursor:pointer;font-weight:600;list-style-position:outside;">
          ${escapeHtml(g.date)} · ${escapeHtml(g.timeSlotLabelIt || g.timeSlot || '')} · ${escapeHtml(g.category || '')} · «${escapeHtml(g.displayName || '')}» · UOC ${escapeHtml(g.uocId || '')} (${g.count} slot)${conflict}
        </summary>
        <table style="width:100%;font-size:0.85rem;margin-top:0.5rem;border-collapse:collapse;">
          <thead><tr><th style="text-align:left">Id slot</th><th>Cat.</th><th>UOC</th><th>Med.</th><th>Lock</th></tr></thead>
          <tbody>${rowCells}</tbody>
        </table>
      </details>
    `;
  }).join('');
  overlay.innerHTML = `
    <div class="modal" style="max-width: 680px; max-height: 90vh; display: flex; flex-direction: column;">
      <div class="modal-header">
        <h3 style="margin:0;font-size:1.05rem;">Duplicati di cella (Piano turni)</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="dedupe-modal-close" aria-label="Chiudi">×</button>
      </div>
      <div class="modal-body" style="overflow:auto;flex:1;min-height:0;">
        <p style="font-size:0.9rem;color:var(--text-secondary);margin:0 0 0.75rem;line-height:1.45;">
          Stessa <strong>data</strong>, <strong>fascia</strong>, <strong>categoria</strong>, <strong>UOC</strong>, <strong>tipo attività</strong> e <strong>nome</strong> (anche vuoto): gruppi <strong>${groupCount}</strong>, slot in eccesso <strong>${totalExtra}</strong>.
          Unificazione: priorità <strong>bloccato</strong>, poi <strong>con assegnazione</strong>, altrimenti rowid minore (come all’avvio backend).
        </p>
        ${groupCount ? '' : '<p style="margin:0 0 0.75rem;">Nessun duplicato logico in <code>shift_slots</code>.</p>'}
        <div id="dedupe-groups-container">${listHtml}</div>
      </div>
      <div class="modal-footer" style="flex-wrap:wrap;gap:0.5rem;">
        <button type="button" class="btn btn-secondary" id="dedupe-modal-cancel">Chiudi</button>
        ${groupCount ? '<button type="button" class="btn btn-danger" id="dedupe-modal-apply">Unifica duplicati (un solo slot per gruppo)</button>' : ''}
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = () => {
    overlay.remove();
    onDone?.();
  };
  overlay.querySelector('#dedupe-modal-close')?.addEventListener('click', close);
  overlay.querySelector('#dedupe-modal-cancel')?.addEventListener('click', close);
  overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(); });
  overlay.querySelector('#dedupe-modal-apply')?.addEventListener('click', async () => {
    if (!groupCount) return;
    if (!confirm('Eliminare i duplicati nel database? Operazione irreversibile (consigliato un checkpoint).')) return;
    const stepOk = await ensureStepUp();
    if (!stepOk) return;
    const applyBtn = overlay.querySelector('#dedupe-modal-apply');
    if (applyBtn instanceof HTMLButtonElement) applyBtn.disabled = true;
    try {
      const res = await api.apiPostShiftSlotsHygieneDedupeSameName();
      await initStore();
      const removed = Number(res?.removedSlots ?? 0);
      const proc = Number(res?.processedGroups ?? 0);
      const moved = Number(res?.movedAssignments ?? 0);
      close();
      alert(
        `Unificazione completata.\n`
        + `· Gruppi elaborati: ${proc}\n`
        + `· Slot rimossi: ${removed}\n`
        + `· Assegnazioni spostate su slot tenuto: ${moved}`,
      );
    } catch (err) {
      alert(err?.message || String(err));
      if (applyBtn instanceof HTMLButtonElement) applyBtn.disabled = false;
    }
  });
}

async function ensureStepUp() {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="modal" style="max-width: 420px;">
        <div class="modal-header">
          <h3 style="margin:0;font-size:1.05rem;">Conferma password admin</h3>
          <button type="button" class="btn btn-secondary btn-sm" id="stepup-close-x" aria-label="Chiudi">×</button>
        </div>
        <form id="stepup-form">
          <div class="modal-body">
            <p style="margin:0 0 0.75rem 0; font-size: 0.92rem; color: var(--text-secondary);">
              Inserisci la password admin per continuare.
            </p>
            <div class="form-group" style="margin-bottom: 0.5rem;">
              <label for="stepup-password">Password</label>
              <input type="password" id="stepup-password" autocomplete="current-password" style="width: 100%; box-sizing: border-box;" />
            </div>
            <div id="stepup-error" style="display:none; color: var(--danger); font-size: 0.9rem;"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" id="stepup-cancel">Annulla</button>
            <button type="submit" class="btn btn-primary" id="stepup-confirm">Conferma</button>
          </div>
        </form>
      </div>
    `;
    document.body.appendChild(overlay);

    const cleanup = () => overlay.remove();
    const close = () => {
      cleanup();
      resolve(false);
    };
    const errEl = overlay.querySelector('#stepup-error');
    const pwdInput = overlay.querySelector('#stepup-password');
    const confirmBtn = overlay.querySelector('#stepup-confirm');
    const cancelBtn = overlay.querySelector('#stepup-cancel');
    const closeBtn = overlay.querySelector('#stepup-close-x');

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    cancelBtn?.addEventListener('click', close);
    closeBtn?.addEventListener('click', close);
    overlay.querySelector('#stepup-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const pwd = String(pwdInput?.value || '');
      if (!pwd.trim()) {
        errEl.textContent = 'Password richiesta.';
        errEl.style.display = 'block';
        pwdInput?.focus();
        return;
      }
      errEl.style.display = 'none';
      if (confirmBtn) confirmBtn.disabled = true;
      if (cancelBtn) cancelBtn.disabled = true;
      if (closeBtn) closeBtn.disabled = true;
      try {
        await api.apiStepUp(pwd);
        cleanup();
        resolve(true);
      } catch (err) {
        errEl.textContent = err?.message || String(err);
        errEl.style.display = 'block';
        if (confirmBtn) confirmBtn.disabled = false;
        if (cancelBtn) cancelBtn.disabled = false;
        if (closeBtn) closeBtn.disabled = false;
        pwdInput?.focus();
      }
    });

    requestAnimationFrame(() => pwdInput?.focus());
  });
}

function formatSecurityDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatLocation(row) {
  const geo = [row.city, row.region, row.country].filter(Boolean);
  const place = geo.length ? ` (${escapeHtml(geo.join(', '))})` : '';
  return `${escapeHtml(row.ip || '—')}${place}`;
}

function renderUsersLastAccessRows(rows, selectedUserId = null) {
  if (!rows?.length) {
    return '<tr><td colspan="6" style="color: var(--text-secondary);">Nessun utente registrato.</td></tr>';
  }
  return rows.map((row) => {
    const hasAccess = !!row.last_access_at;
    const locRow = {
      ip: row.ip,
      city: row.city,
      region: row.region,
      country: row.country,
    };
    const ua = row.user_agent || '';
    const fullHash = row.device_hash || '';
    const dhShort = fullHash ? `${fullHash.slice(0, 12)}…` : '—';
    const tipRaw = [fullHash ? `Fingerprint: ${fullHash}` : '', ua ? `UA: ${ua}` : ''].filter(Boolean).join(' · ');
    const tip = escapeHtml(tipRaw.slice(0, 500));
    const selected = selectedUserId && row.user_id === selectedUserId;
    const inst = hasAccess ? instanceLabelFromEvent({ details: row.details }) : '—';
    return `
    <tr class="users-last-access-row${selected ? ' users-last-access-row--selected' : ''}" data-user-id="${escapeHtml(row.user_id)}" style="cursor: pointer;" title="Clic per dettaglio utente">
      <td>${escapeHtml(row.username || '—')}</td>
      <td>${escapeHtml(getRoleLabel(row.user_role || ''))}</td>
      <td>${hasAccess ? escapeHtml(formatSecurityDate(row.last_access_at)) : '<span style="color: var(--text-secondary);">Mai</span>'}</td>
      <td>${escapeHtml(inst)}</td>
      <td>${hasAccess ? formatLocation(locRow) : '—'}</td>
      <td title="${tip}">${hasAccess ? escapeHtml(dhShort) : '—'}</td>
    </tr>`;
  }).join('');
}

function renderSecurityEvents(events) {
  if (!events?.length) return '<tr><td colspan="8" style="color: var(--text-secondary);">Nessun evento.</td></tr>';
  return events.map((e) => {
    const ua = e.user_agent || '';
    const fullHash = e.device_hash || '';
    const dhShort = fullHash ? `${fullHash.slice(0, 12)}…` : '—';
    const tipRaw = [fullHash ? `Fingerprint: ${fullHash}` : '', ua ? `UA: ${ua}` : ''].filter(Boolean).join(' · ');
    const tip = escapeHtml(tipRaw.slice(0, 500));
    return `
    <tr>
      <td>${escapeHtml(formatSecurityDate(e.created_at))}</td>
      <td>${escapeHtml(instanceLabelFromEvent(e))}</td>
      <td>${escapeHtml(e.event_type || '—')}</td>
      <td>${escapeHtml(e.username_attempt || e.user_id || '—')}</td>
      <td>${e.success ? '<span style="color: var(--success);">OK</span>' : '<span style="color: var(--danger);">KO</span>'}</td>
      <td>${formatLocation(e)}</td>
      <td title="${tip}">${escapeHtml(dhShort)}</td>
      <td>${escapeHtml((e.risk_flags || []).join(', ') || '—')}</td>
    </tr>`;
  }).join('');
}

function renderSecurityDevices(devices, users = []) {
  if (!devices?.length) return '<tr><td colspan="5" style="color: var(--text-secondary);">Nessun dispositivo noto.</td></tr>';
  return devices.map(d => `
    <tr>
      <td>${escapeHtml(d.username || users.find(u => u.id === d.user_id)?.username || d.user_id || '—')}</td>
      <td>${escapeHtml(formatSecurityDate(d.first_seen_at))}</td>
      <td>${escapeHtml(formatSecurityDate(d.last_seen_at))}</td>
      <td>${escapeHtml(d.last_ip || '—')} ${[d.city, d.region, d.country].filter(Boolean).length ? `(${escapeHtml([d.city, d.region, d.country].filter(Boolean).join(', '))})` : ''}</td>
      <td title="${escapeHtml(d.device_hash || '')}">${escapeHtml((d.device_hash || '').slice(0, 12) || '—')}</td>
    </tr>
  `).join('');
}

function renderSecuritySessionsRows(sessions) {
  if (!sessions?.length) return '<tr><td colspan="6" style="color: var(--text-secondary);">Nessuna sessione.</td></tr>';
  return sessions.map(s => `
    <tr>
      <td>${escapeHtml(s.username || s.user_id || '—')}</td>
      <td>${escapeHtml(formatSecurityDate(s.last_seen_at))}</td>
      <td>${formatLocation(s)}</td>
      <td>${escapeHtml((s.user_agent || '').slice(0, 48) || '—')}</td>
      <td>${s.revoked_at ? '<span style="color: var(--danger);">Revocata</span>' : '<span style="color: var(--success);">Attiva</span>'}</td>
      <td>${s.revoked_at ? '—' : `<button class="btn btn-danger btn-sm btn-revoke-session" data-id="${escapeHtml(s.jti)}">Revoca</button>`}</td>
    </tr>
  `).join('');
}

function bindRevokeSessionButtons(container, onDone) {
  container.querySelectorAll('.btn-revoke-session').forEach(btn => {
    btn.addEventListener('click', async () => {
      const reason = prompt('Motivo revoca (opzionale):', 'Revocata da admin') || '';
      if (!confirm('Revocare questa sessione?')) return;
      try {
        await api.apiRevokeSecuritySession(btn.dataset.id, reason);
        onDone?.();
      } catch (err) {
        alert(err?.message || err);
      }
    });
  });
}

function showSecurityDetailsModal(container, user, details) {
  const pager = {
    events: { cursor: details.eventsNextCursor || null, done: !details.eventsNextCursor, loading: false, items: details.events || [] },
    devices: { cursor: details.devicesNextCursor || null, done: !details.devicesNextCursor, loading: false, items: details.devices || [] },
    alerts: { cursor: details.alertsNextCursor || null, done: !details.alertsNextCursor, loading: false, items: details.alerts || [] },
    sessions: { cursor: details.sessionsNextCursor || null, done: !details.sessionsNextCursor, loading: false, items: details.sessions || [] },
  };
  const modal = document.createElement('div');
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.45);display:flex;align-items:flex-start;justify-content:center;z-index:9999;padding:2rem;overflow:auto;';
  modal.innerHTML = `
    <div class="card" style="width:min(1100px,95vw);">
      <div class="card-title">Dettaglio monitoraggio accessi — ${escapeHtml(user?.username || 'utente')}</div>
      <div style="display:flex;gap:1rem;flex-wrap:wrap;">
        <div style="flex:1;min-width:320px;">
          <h4 style="margin:0 0 0.5rem 0;">Eventi</h4>
          <div class="table-wrapper" style="max-height:260px;overflow:auto;">
            <table><thead><tr><th>Quando</th><th>Piattaforma</th><th>Tipo</th><th>Esito</th><th>IP / Località</th><th>Dispositivo</th><th>Rischio</th></tr></thead>
            <tbody id="details-events-body">${renderEventRows(pager.events.items)}</tbody></table>
          </div>
          <button class="btn btn-secondary btn-sm" id="btn-load-more-events" style="margin-top:0.5rem;">Carica altro</button>
        </div>
        <div style="flex:1;min-width:320px;">
          <h4 style="margin:0 0 0.5rem 0;">Dispositivi</h4>
          <div class="table-wrapper" style="max-height:260px;overflow:auto;">
            <table><thead><tr><th>First seen</th><th>Last seen</th><th>IP/Località</th><th>Fingerprint</th></tr></thead>
            <tbody id="details-devices-body">${renderDeviceRows(pager.devices.items)}</tbody></table>
          </div>
          <button class="btn btn-secondary btn-sm" id="btn-load-more-devices" style="margin-top:0.5rem;">Carica altro</button>
        </div>
      </div>
      <div style="display:flex;gap:1rem;flex-wrap:wrap;margin-top:1rem;">
        <div style="flex:1;min-width:320px;">
          <h4 style="margin:0 0 0.5rem 0;">Alert</h4>
          <div class="table-wrapper" style="max-height:220px;overflow:auto;">
            <table><thead><tr><th>Quando</th><th>Tipo</th><th>Messaggio</th><th>Stato</th></tr></thead>
            <tbody id="details-alerts-body">${renderAlertRows(pager.alerts.items)}</tbody></table>
          </div>
          <button class="btn btn-secondary btn-sm" id="btn-load-more-alerts" style="margin-top:0.5rem;">Carica altro</button>
        </div>
        <div style="flex:1;min-width:320px;">
          <h4 style="margin:0 0 0.5rem 0;">Sessioni</h4>
          <div class="table-wrapper" style="max-height:220px;overflow:auto;">
            <table><thead><tr><th>Utente</th><th>Ultima attività</th><th>IP / Località</th><th>UA</th><th>Stato</th><th></th></tr></thead>
            <tbody id="details-sessions-body">${renderSecuritySessionsRows(pager.sessions.items)}</tbody></table>
          </div>
          <button class="btn btn-secondary btn-sm" id="btn-load-more-sessions" style="margin-top:0.5rem;">Carica altro</button>
        </div>
      </div>
      <div style="display:flex;justify-content:flex-end;margin-top:1rem;">
        <button class="btn btn-secondary" id="security-details-close">Chiudi</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  modal.querySelector('#security-details-close')?.addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
  bindRevokeSessionButtons(modal, () => {
    refreshMonitoringSection(container).catch(() => {});
  });

  const bindLoad = (kind, buttonSelector, bodySelector, loader, renderer) => {
    const btn = modal.querySelector(buttonSelector);
    const body = modal.querySelector(bodySelector);
    if (!btn || !body) return;
    const refreshButton = () => {
      btn.disabled = pager[kind].loading || pager[kind].done;
      btn.textContent = pager[kind].done ? 'Fine elenco' : (pager[kind].loading ? 'Caricamento...' : 'Carica altro');
    };
    refreshButton();
    btn.addEventListener('click', async () => {
      if (pager[kind].loading || pager[kind].done) return;
      pager[kind].loading = true;
      refreshButton();
      try {
        const page = await loader(pager[kind].cursor);
        const items = page?.items || [];
        pager[kind].items = pager[kind].items.concat(items);
        pager[kind].cursor = page?.nextCursor || null;
        pager[kind].done = !pager[kind].cursor;
        body.innerHTML = renderer(pager[kind].items);
        if (kind === 'sessions') bindRevokeSessionButtons(modal, () => {});
      } catch (err) {
        alert(err?.message || err);
      } finally {
        pager[kind].loading = false;
        refreshButton();
      }
    });
  };

  bindLoad(
    'events',
    '#btn-load-more-events',
    '#details-events-body',
    (cursor) => api.apiGetSecurityEventsPage({
      userId: user?.id,
      riskOnly: securityUiState.riskOnly,
      eventType: securityUiState.eventTypeFilter || undefined,
      fromCreatedAt: securityUiState.dateFrom ? dateInputToIsoStart(securityUiState.dateFrom) : undefined,
      toCreatedAt: securityUiState.dateTo ? dateInputToIsoEnd(securityUiState.dateTo) : undefined,
      limit: 30,
      cursorCreatedAt: cursor?.createdAt,
      cursorId: cursor?.id,
    }),
    renderEventRows,
  );
  bindLoad(
    'devices',
    '#btn-load-more-devices',
    '#details-devices-body',
    (cursor) => api.apiGetSecurityDevicesPage({
      userId: user?.id,
      limit: 30,
      cursorLastSeenAt: cursor?.lastSeenAt,
      cursorDeviceHash: cursor?.deviceHash,
    }),
    renderDeviceRows,
  );
  bindLoad(
    'alerts',
    '#btn-load-more-alerts',
    '#details-alerts-body',
    (cursor) => api.apiGetSecurityAlertsPage({
      limit: 30,
      cursorCreatedAt: cursor?.createdAt,
      cursorId: cursor?.id,
    }),
    renderAlertRows,
  );
  bindLoad(
    'sessions',
    '#btn-load-more-sessions',
    '#details-sessions-body',
    (cursor) => api.apiGetSecuritySessionsPage({
      userId: user?.id,
      limit: 30,
      cursorLastSeenAt: cursor?.lastSeenAt,
      cursorJti: cursor?.jti,
    }),
    renderSecuritySessionsRows,
  );
}

function renderEventRows(events) {
  if (!(events || []).length) return '<tr><td colspan="7" style="color: var(--text-secondary);">Nessun dato</td></tr>';
  return (events || []).map((e) => {
    const ua = e.user_agent || '';
    const fullHash = e.device_hash || '';
    const dhShort = fullHash ? `${fullHash.slice(0, 10)}…` : '—';
    const tipRaw = [fullHash ? `Fingerprint: ${fullHash}` : '', ua ? `UA: ${ua}` : ''].filter(Boolean).join(' · ');
    const tip = escapeHtml(tipRaw.slice(0, 500));
    return `<tr><td>${escapeHtml(formatSecurityDate(e.created_at))}</td><td>${escapeHtml(instanceLabelFromEvent(e))}</td><td>${escapeHtml(e.event_type || '—')}</td><td>${e.success ? 'OK' : 'KO'}</td><td>${formatLocation(e)}</td><td title="${tip}">${escapeHtml(dhShort)}</td><td>${escapeHtml((e.risk_flags || []).join(', ') || '—')}</td></tr>`;
  }).join('');
}

function renderDeviceRows(devices) {
  return (devices || []).map(d => `<tr><td>${escapeHtml(formatSecurityDate(d.first_seen_at))}</td><td>${escapeHtml(formatSecurityDate(d.last_seen_at))}</td><td>${escapeHtml(d.last_ip || '—')} ${[d.city, d.region, d.country].filter(Boolean).length ? `(${escapeHtml([d.city, d.region, d.country].filter(Boolean).join(', '))})` : ''}</td><td title="${escapeHtml(d.device_hash || '')}">${escapeHtml((d.device_hash || '').slice(0, 16) || '—')}</td></tr>`).join('') || '<tr><td colspan="4">Nessun dato</td></tr>';
}

function renderAlertRows(alerts) {
  return (alerts || []).map(a => `<tr><td>${escapeHtml(formatSecurityDate(a.created_at))}</td><td>${escapeHtml(a.alert_type || '—')}</td><td>${escapeHtml(a.message || '')}</td><td>${escapeHtml(a.status || 'new')}</td></tr>`).join('') || '<tr><td colspan="4">Nessun dato</td></tr>';
}

function showUserForm(container, user) {
  const state = getState();
  const isNew = !user;
  document.getElementById('user-form-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'user-form-modal';
  modal.className = 'modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', 'user-form-modal-title');
  modal.innerHTML = `
    <div class="modal" style="max-width: 440px;">
      <div class="modal-header">
        <h3 id="user-form-modal-title" style="margin:0;font-size:1rem;">${isNew ? 'Nuovo utente' : 'Modifica utente'}</h3>
        <button type="button" class="btn btn-secondary btn-sm" id="user-form-close-x" aria-label="Chiudi">×</button>
      </div>
      <form id="user-form" class="modal-body" style="display: flex; flex-direction: column; gap: 1rem;">
        <div class="form-group">
          <label for="user-username">Username</label>
          <input type="text" id="user-username" value="${user ? escapeHtml(user.username) : ''}" required style="width: 100%; padding: 0.5rem;" ${!isNew ? '' : 'autofocus'}>
        </div>
        <div class="form-group">
          <label for="user-password">Password ${isNew ? '' : '(lascia vuoto per non cambiare)'}</label>
          <input type="password" id="user-password" placeholder="${isNew ? 'Password' : 'Nuova password'}" style="width: 100%; padding: 0.5rem;">
        </div>
        <div class="form-group">
          <label for="user-role">Ruolo</label>
          <select id="user-role" style="width: 100%; padding: 0.5rem;">
            ${ROLES.map(r => `<option value="${r.value}" ${user?.role === r.value ? 'selected' : ''}>${r.label}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" id="doctor-id-group">
          <label for="user-doctor">Medico collegato</label>
          <select id="user-doctor" style="width: 100%; padding: 0.5rem;">
            <option value="">— Nessuno —</option>
            ${state.doctors.map(d => `<option value="${d.id}" ${user?.doctorId === d.id ? 'selected' : ''}>${d.sigla} - ${d.cognome}</option>`).join('')}
          </select>
          <small style="color: var(--text-secondary);">Obbligatorio per Medico. Opzionale per Admin e Organizzatore.</small>
        </div>
        <div class="form-group">
          <label>Recapiti email</label>
          <div id="user-emails-editor"></div>
        </div>
        <div id="user-form-error" style="color: var(--danger); font-size: 0.9rem; display: none;"></div>
      </form>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" id="user-form-cancel">Annulla</button>
        <button type="submit" class="btn btn-primary" id="user-form-submit" form="user-form">${isNew ? 'Crea' : 'Salva'}</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  renderEmailRecipientsEditor(
    modal.querySelector('#user-emails-editor'),
    Array.isArray(user?.emails) && user.emails.length
      ? user.emails
      : (user?.email ? [{ email: user.email, isPrimary: true, recvShiftPdf: true, recvBoard: true }] : []),
  );
  const prevBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const closeModal = () => {
    document.body.style.overflow = prevBodyOverflow;
    modal.remove();
  };

  modal.querySelector('#user-form-cancel')?.addEventListener('click', closeModal);
  modal.querySelector('#user-form-close-x')?.addEventListener('click', closeModal);

  modal.querySelector('#user-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const username = modal.querySelector('#user-username').value.trim();
    const password = modal.querySelector('#user-password').value;
    const role = modal.querySelector('#user-role').value;
    const doctorId = modal.querySelector('#user-doctor').value || null;
    const emails = collectEmailRecipients(modal.querySelector('#user-emails-editor'));
    const errEl = modal.querySelector('#user-form-error');

    if (!username) {
      errEl.textContent = 'Username obbligatorio.';
      errEl.style.display = 'block';
      return;
    }
    if (role === 'user' && !doctorId) {
      errEl.textContent = 'Il medico user deve essere collegato a un medico.';
      errEl.style.display = 'block';
      return;
    }
    if (isNew && !password) {
      errEl.textContent = 'Password obbligatoria per i nuovi utenti.';
      errEl.style.display = 'block';
      return;
    }
    const emailsError = validateEmailRecipients(emails);
    if (emailsError) {
      errEl.textContent = emailsError;
      errEl.style.display = 'block';
      return;
    }

    errEl.style.display = 'none';
    const submitBtn = modal.querySelector('#user-form-submit');
    if (submitBtn) submitBtn.disabled = true;

    try {
      if (isNew) {
        await api.apiCreateUser({ username, password, role, doctorId, emails });
      } else {
        const updates = { username, role, doctorId, emails };
        if (password) updates.password = password;
        await api.apiUpdateUser(user.id, updates);
      }
      closeModal();
      renderUsersView(container);
    } catch (err) {
      errEl.textContent = err?.message || err;
      errEl.style.display = 'block';
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}
